/**
 * Default home route derivation — single source of truth.
 *
 * "Where does this user belong?" is asked in two places:
 *  - ProtectedRoute, when a user fails a route's access requirement and must be
 *    bounced somewhere they can actually use.
 *  - The context switch flow, which must land the user on a valid page for the
 *    new context (their previous route may not exist/apply there).
 *
 * Both must agree, so the logic lives here once.
 *
 * Effective level is what the server enforces (`AccessContext.level`):
 *  - network context → the network seat's role (#1322). A role that is missing
 *    or not one of admin/manager/viewer is `viewer` (least privilege) and is
 *    reported once.
 *  - else the org-context level (preferred)
 *  - else the user's top-level level (fallback)
 *
 * `isAdmin` / `isManager` / `isViewer` in auth/authData.ts are expressed
 * through the same derivation, so nothing can disagree with it.
 *
 * Default route: viewers land on the dashboard; everyone else on the root.
 */

import { paths } from '@/constants';
import { reportClientError } from '@/api/clientErrors';
import { isViewPath } from '@/lib/validateRedirect';
import type { User, UserLevel } from '@/types';

/** Role values already reported this session: one report per distinct value, not per render. */
const reportedBadRoles = new Set<string>();

/** Test seam: forget which bad roles were reported. */
export function _resetNetworkRoleReports(): void {
  reportedBadRoles.clear();
}

const LEVELS: readonly UserLevel[] = ['admin', 'manager', 'viewer'];

function isUserLevel(value: unknown): value is UserLevel {
  return typeof value === 'string' && (LEVELS as readonly string[]).includes(value);
}

/**
 * Derive the user's effective level for routing/access decisions.
 *
 * In a network context the level is the network seat's role, the same as the
 * server's `AccessContext.level`. Otherwise prefer the org-context level,
 * falling back to the top-level level.
 */
export function getEffectiveLevel(user: User): UserLevel | null {
  if (user.context?.type === 'network') {
    const role: unknown = user.context.network?.role;
    if (isUserLevel(role)) return role;
    // Least privilege, but never silently: the server always sends a role.
    const shown = JSON.stringify(role ?? null).slice(0, 60);
    if (!reportedBadRoles.has(shown)) {
      reportedBadRoles.add(shown);
      const message = `Unknown network role: ${shown}`;
      reportClientError({ error: new Error(message), message, source: 'auth' });
    }
    return 'viewer';
  }
  return user.context?.organization?.level ?? user.level;
}

/**
 * #1400: a login that belongs to no company. `has_active_membership` is the
 * server's answer (vingapi#1858); never infer it from `context.type` (a
 * non-staff login with two memberships and no context still gets the chooser)
 * or from `level` alone. A user object without the field (older cached shape)
 * is NOT a no-company login.
 */
export function isNoCompanyLogin(user: User): boolean {
  return user.isStaff !== true && user.hasActiveMembership === false;
}

/**
 * The default home route for a user given their effective level.
 *
 * Viewers and no-company logins → dashboard; admin/manager → root.
 */
export function getDefaultRoute(user: User): string {
  if (isNoCompanyLogin(user)) return paths.viewerHome;
  return getEffectiveLevel(user) === 'viewer' ? paths.dashboard : paths.root;
}

/**
 * Where an authenticated user should land, given a requested redirect.
 *
 * /view/ share targets are always honored. Redirects carrying a `ctx` query
 * param are also honored unchanged so the CtxParamGate can switch context
 * before the inner route renders (#404). Otherwise EFFECTIVE viewers go to
 * the viewer home instead of the raw redirect, whose desktop default is '/'
 * — the admin/manager dashboard. Every post-auth navigation (SignIn's
 * password/merge/already-authed paths, the Sso callback) must route
 * through this so the flows can't drift.
 */
export function landingPath(redirectPath: string, user: User | null | undefined): string {
  if (isViewPath(redirectPath)) return redirectPath;

  // #1400: no membership, so ?ctx= is irrelevant; straight to the viewer home.
  if (user && isNoCompanyLogin(user)) return paths.viewerHome;

  // A redirect carrying ?ctx=… must reach the CtxParamGate unchanged so it
  // can switch context before any per-route access check runs. Without this,
  // a viewer signing in from a notification link (e.g. ?ctx=org:321) would
  // be bounced to the viewer dashboard in the wrong context.
  // Key name: 'ctx' — see src/features/context/ctxParam.ts for the canonical constant.
  const searchStart = redirectPath.indexOf('?');
  if (searchStart !== -1) {
    const params = new URLSearchParams(redirectPath.slice(searchStart));
    if (params.has('ctx')) return redirectPath;
  }

  return user && getEffectiveLevel(user) === 'viewer' ? paths.viewerHome : redirectPath;
}
